import { AppColors } from './utils';
import { toast } from 'react-toastify';

export const getCapitalizedString = (value: string) => {
  if (value.length > 0) {
    return capitalize('\n', capitalize('. ', value));
  }
  return value;
};

const capitalize = (splitChar: string, value: string): string => {
  return value
    .split(splitChar)
    .map((part) => {
      const trimmed = part.trimStart();
      if (!trimmed) return part; // Preserve empty or whitespace-only segments
      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    })
    .join(splitChar);
};

export const getRandomIntNumber = (max: number): number => {
  return Math.floor(Math.random() * max);
};

export const urlToFileObject = async (url: string, fileName: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const file = new File([blob], fileName, { type: blob.type });
    return file;
  } catch (e) {}
  return null;
};

export const secondsToHMS = (seconds: number) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor((seconds % 60) / 100);

  const hoursString = hours > 0 ? hours + ':' : '';
  const minutesString = (minutes < 10 ? '0' : '') + minutes + ':';
  const secondsString = (remainingSeconds < 10 ? '0' : '') + remainingSeconds;

  return hoursString + minutesString + secondsString;
};

export const openLinkOnNewTab = (link: string) => {
  window.open(`${link}`, '_blank') || window.location.replace(`${link}`);
};

export const copyText = (text: string) => {
  navigator.clipboard.writeText(text);
  showSuccessMessage('Copied');
};

export const showSuccessMessage = (message: string) => {
  toast.success(message);
};

export const showErrorMessage = (message: string) => {
  toast.error(message);
};

export const formatReportCount = (data: number) => {
  const SI_SYMBOL = ['', 'k', 'm', 'b', 't'];

  // what tier? (determines SI symbol)
  const tier = (Math.log10(Math.abs(data)) / 3) | 0;

  // if zero, we don't need a suffix
  if (tier === 0) return data.toString();

  // get suffix and determine scale
  const suffix = SI_SYMBOL[tier];
  const scale = Math.pow(10, tier * 3);

  // scale the number
  const scaled = data / scale;

  // format number and add suffix
  return scaled.toFixed(1) + suffix;
};

// Format the number as US standard
export const formatNumber = (num?: number) => {
  // const parsedNum = parseFloat(num);
  if (isNaN(num || 0)) return '';
  return new Intl.NumberFormat('en-US', { style: 'decimal' }).format(num || 0);
};

export const priceFormatter = (amount: number) => {
  return new Intl.NumberFormat('en-US', {
    // style: 'decimal',
    // style: 'currency'
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
};

export const isLandingPageShareItem = (type: string) => {
  return [
    'affiliate_landing_page',
    'rejection_free_landing_page',
    'network_landing_page',
    'group_landing_page',
  ].includes(type);
};

export const cleanPhoneNumber = (phoneNumber: string) => {
  let cleanedNumber = phoneNumber.replace(/[-()]/g, '');
  return cleanedNumber;
};

export const formatPhoneNumber = (number: string) => {
  // Remove non-digits
  const cleaned = number.replace(/\D/g, '');

  if (cleaned.length < 7) return number;

  const part1 = cleaned.slice(0, 3);
  const part2 = cleaned.slice(3, 6);
  const part3 = cleaned.slice(6); // rest of the number

  return [part1, part2, part3].filter(Boolean).join('-');
};

// Sample output: (949)212-1111
export const formatPhoneNumber2 = (value: string) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // Extract the first 10 digits
  const mainNumber = digits.slice(0, 10);
  const extraDigits = digits.slice(10);

  const match = mainNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);

  let formatted = '';
  if (!match) return value;

  if (match[1]) formatted += `(${match[1]}`;
  if (match[1].length === 3) formatted += ')';
  if (match[2]) formatted += match[1].length === 3 ? match[2] : `)${match[2]}`;
  if (match[3]) formatted += `-${match[3]}`;

  // Append extra digits, if any
  if (extraDigits) {
    formatted += ` ext${extraDigits}`;
  }

  return formatted;
};

export const calculateReportPercentageDiff = (
  valueOne: number,
  valueTwo: number
): number => {
  var percentage = 0.0;

  if (valueOne === 0 && valueTwo === 0) {
    percentage = 0.0;
  } else if (valueOne <= 0) {
    percentage = 100.0;
  } else if (valueTwo <= 0) {
    percentage = -100.0;
  } else {
    percentage = ((valueTwo - valueOne) / valueOne) * 100;
  }
  return parseFloat(percentage.toFixed(1));
};

export const getTimestamp = (): string => {
  const now = new Date();
  const pad = (n: number) => n.toString().padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
    now.getDate()
  )}`;
  const time = `${pad(now.getHours())}-${pad(now.getMinutes())}-${pad(
    now.getSeconds()
  )}`;
  return `${date}_${time}`;
};

export const inputTextFieldStyle = () => {
  return {
    borderRadius: '4px',
    backgroundColor: AppColors.grayZeroColor,
    '& label': {
      color: AppColors.grayThreeColor,
    },
    '& .MuiOutlinedInput-root input': {
      color: AppColors.grayThreeColor,
      '-webkit-text-fill-color': AppColors.grayThreeColor,
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-root fieldset': {
      'border-color': `${AppColors.strokeColor} !important`,
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
      '&:hover fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
    },
    '& fieldset.MuiOutlinedInput-notchedOutline': {
      borderColor: `${AppColors.strokeColor} !important`,
    },
  };
};

export const inputTextFieldStyle2 = () => {
  return {
    borderRadius: '4px',
    backgroundColor: AppColors.whiteColor,
    '& label': {
      color: AppColors.primaryColor,
    },
    '& .MuiOutlinedInput-root input': {
      color: AppColors.grayThreeColor,
      '-webkit-text-fill-color': AppColors.grayThreeColor,
      WebkitTextFillColor: AppColors.grayThreeColor,
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-root fieldset': {
      'border-color': `${AppColors.strokeColor} !important`,
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
      '&:hover fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
    },
    '& fieldset.MuiOutlinedInput-notchedOutline': {
      borderColor: `${AppColors.strokeColor} !important`,
    },
  };
};

export const inputTextFieldStyleTeamSetup = () => {
  return {
    borderRadius: '12px',
    backgroundColor: AppColors.grayZeroColor,
    '& label': {
      color: AppColors.grayThreeColor,
    },
    '& .MuiOutlinedInput-root input': {
      color: AppColors.grayThreeColor,
      '-webkit-text-fill-color': AppColors.grayThreeColor,
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-root fieldset': {
      'border-color': `${AppColors.strokeColor} !important`,
      borderWidth: '1px !important',
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
      '&:hover fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: `${AppColors.strokeColor} !important`,
        borderWidth: '1px !important',
      },
    },
    '& fieldset.MuiOutlinedInput-notchedOutline': {
      borderColor: `${AppColors.strokeColor} !important`,
    },
  };
};
